"use client"

import type React from "react"

import { type FormEvent, useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Card, Form, Button, Spinner, Toast } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { LanguageSwitcher } from "../../components/language-switcher"
import { PasswordInput } from "../../components/ui/password-input"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { useAuth } from "../../contexts/auth-context"
import { Upload } from "lucide-react"
import { fetchPublicBuildings } from "../../redux/slices/buildingsSlice"
import { validateAndResizeImage, getImageValidationLimits } from "../../utils/image-validation"

export default function RegisterPage() {
  const dispatch = useAppDispatch()
  const { register, isLoading, error, clearError } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success")
  const { buildings = [] } = useAppSelector((state) => state.buildings)

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    phoneNumber: "",
    building: "",
    roomNumber: "",
    registrationCode: "",
  })
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)

  const { maxFileSizeKB, maxDimension } = getImageValidationLimits()

  useEffect(() => {
    // Use fetchPublicBuildings instead of fetchBuildings since this is a public page
    dispatch(fetchPublicBuildings())
  }, [dispatch])

  useEffect(() => {
    if (error) {
      setToastVariant("danger")
      setToastMessage(error)
      setShowToast(true)
      clearError()
    }
  }, [error, clearError])

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]

      const validatedFile = await validateAndResizeImage(file)
        .then((resizedFile) => {
          setProfilePhoto(resizedFile)
          setPhotoPreview(URL.createObjectURL(resizedFile))
          setPhotoError(null)
          return resizedFile
        })
        .catch((error) => {
          setPhotoError(error.message)
          setProfilePhoto(null)
          setPhotoPreview(null)
          e.target.value = ""
          return null
        })
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!profilePhoto) {
      setToastVariant("danger")
      setToastMessage("Profile photo is required")
      setShowToast(true)
      return
    }

    try {
      await register({
        ...formData,
        profilePhoto,
      })
      setToastVariant("success")
      setToastMessage("Registration successful! Your account is pending approval.")
      setShowToast(true)
      navigate("/auth/pending-approval")
    } catch (error: any) {
      // Error handling is done in the auth context and useEffect above
    }
  }

  return (
    <div className="gradient-page-centered p-3">
      <div className="position-absolute top-0 end-0 p-3">
        <LanguageSwitcher />
      </div>

      <Card
        className="w-100 border-0 shadow-lg"
        style={{
          maxWidth: "520px",
          borderRadius: "16px",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <h3 className="fw-bold mb-1" style={{ color: "#101e46" }}>
              {t("register")}
            </h3>
          </div>

          <Form onSubmit={handleSubmit}>
            <div
              className="d-flex flex-column align-items-center mb-4 p-3 rounded-3"
              style={{ backgroundColor: "rgba(248, 250, 252, 0.8)" }}
            >
              <Form.Label className="fw-medium mb-3" style={{ color: "#2f3b5e" }}>
                {t("profilePhoto")} <span className="text-danger">*</span>
              </Form.Label>
              <div className="position-relative mb-3" style={{ width: "100px", height: "100px" }}>
                {photoPreview ? (
                  <img
                    src={photoPreview || "/placeholder.svg"}
                    alt="Profile Preview"
                    className="rounded-circle w-100 h-100 object-fit-cover shadow-sm"
                    style={{ border: "3px solid #3c82f5" }}
                  />
                ) : (
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center w-100 h-100 shadow-sm"
                    style={{ backgroundColor: "rgba(60, 130, 245, 0.1)", border: "2px dashed #3c82f5" }}
                  >
                    <Upload size={32} style={{ color: "#3c82f5" }} />
                  </div>
                )}
              </div>
              <Form.Control
                id="profilePhoto"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="visually-hidden"
                aria-label="Profile photo"
                required
              />
              <Form.Label htmlFor="profilePhoto" className="btn fw-medium shadow-sm cursor-pointer btn-secondary">
                {t("uploadPhoto")}
              </Form.Label>
              <div className="text-center mt-2">
                <small className="text-muted">
                  Max dimensions: {maxDimension}x{maxDimension}px (will be resized automatically)
                </small>
              </div>
              {photoError ? (
                <div className="text-danger small mt-1">{photoError}</div>
              ) : !profilePhoto ? (
                <div className="text-danger small mt-1">{t("profilePhotoRequired") || "Profile photo is required"}</div>
              ) : null}
            </div>

            <div className="row">
              <div className="col-12 mb-3">
                <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("registrationCode")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="registrationCode"
                  value={formData.registrationCode}
                  onChange={(e: any) => setFormData({ ...formData, registrationCode: e.target.value })}
                  placeholder={t("enterRegistrationCode")}
                  required
                  className="border-0 shadow-sm"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(60, 130, 245, 0.2)",
                  }}
                />
                <Form.Text className="text-muted">{t("registrationCodeHelp")}</Form.Text>
              </div>

              <div className="col-12 mb-3">
                <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("fullName")}
                </Form.Label>
                <Form.Control
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e: any) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  className="border-0 shadow-sm"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(60, 130, 245, 0.2)",
                  }}
                />
              </div>

              <div className="col-12 mb-3">
                <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("emailAddress")}
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e: any) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="border-0 shadow-sm"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(60, 130, 245, 0.2)",
                  }}
                />
              </div>

              <div className="col-12 mb-3">
                <Form.Label htmlFor="register-password" className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("password")}
                </Form.Label>
                <PasswordInput
                  id="register-password"
                  name="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("phoneNumber")}
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e: any) => setFormData({ ...formData, phoneNumber: e.target.value })}
                  required
                  className="border-0 shadow-sm"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(60, 130, 245, 0.2)",
                  }}
                />
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("roomNumber")}
                </Form.Label>
                <Form.Control
                  name="roomNumber"
                  value={formData.roomNumber}
                  onChange={(e: any) => setFormData({ ...formData, roomNumber: e.target.value })}
                  required
                  className="border-0 shadow-sm"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(60, 130, 245, 0.2)",
                  }}
                />
              </div>

              <div className="col-12 mb-4">
                <Form.Label className="fw-medium" style={{ color: "#2f3b5e" }}>
                  {t("building")}
                </Form.Label>
                <Form.Select
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                  required
                  className="border-0 shadow-sm"
                  style={{
                    borderRadius: "12px",
                    padding: "12px 16px",
                    fontSize: "16px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid rgba(60, 130, 245, 0.2)",
                  }}
                >
                  <option value="">{t("selectBuilding")}</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <Button type="submit" className="w-100 fw-medium shadow-sm btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {t("loading")}
                </>
              ) : (
                t("registerButton")
              )}
            </Button>
          </Form>

          <div className="mt-4 text-center">
            <span style={{ color: "#64748b", fontSize: "14px" }}>{t("alreadyHaveAccount")} </span>
            <Link to="/auth/login" className="text-decoration-none fw-medium" style={{ color: "#3c82f5" }}>
              {t("login")}
            </Link>
          </div>
        </Card.Body>
      </Card>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={3000}
        autohide
        className="position-fixed bottom-0 end-0 m-3"
        bg={toastVariant}
      >
        <Toast.Header>
          <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
        </Toast.Header>
        <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
      </Toast>
    </div>
  )
}
